import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import NZTerritoryMap from "@/components/NZTerritoryMap";
import { MapPin, Sprout, Mail, ArrowRight, Sparkles, Clock } from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";

const STATUS_META = {
  open:    { label: "Open",    pill: "bg-[var(--bloom-brand)] text-white", desc: "Taking subscribers now" },
  pending: { label: "Coming soon", pill: "bg-[#C9A14A] text-white",        desc: "Collecting interest" },
  looking: { label: "Looking for a grower", pill: "bg-white border border-[var(--bloom-ink-soft)] text-[var(--bloom-ink-soft)]", desc: "Apply to grow with us" },
};

export default function TerritoriesPage() {
  const [areas, setAreas] = useState([]);
  const [totals, setTotals] = useState({});
  const [activeKey, setActiveKey] = useState(null);
  const { t } = useSiteContent();

  useEffect(() => {
    api.get("/public/area-map").then(({ data }) => {
      setAreas(data.areas || []);
      setTotals(data.totals || {});
    });
  }, []);

  const grouped = useMemo(() => {
    return {
      open:    areas.filter((a) => a.status === "open"),
      pending: areas.filter((a) => a.status === "pending"),
      looking: areas.filter((a) => a.status === "looking"),
    };
  }, [areas]);

  return (
    <div className="club-theme min-h-screen" data-testid="territories-page">
      <PublicHeader />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-12">
        <div className="bloom-divider mb-6">✦ {t("territories.eyebrow", "Chapter III · Aotearoa")} ✦</div>
        <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] max-w-3xl">
          {t("territories.headline", "Our corners of the country.")}
        </h1>
        <p className="font-serif italic text-xl text-[var(--bloom-ink-soft)] mt-6 max-w-2xl">
          {t("territories.sub", "The Bloom Club grows region by region. Each territory has its own grower, its own season, and its own rhythm.")}
        </p>
        <div className="mt-10 flex flex-wrap items-end gap-x-10 gap-y-4" data-testid="territory-stats">
          <Stat n={totals.open || 0} l="open areas" />
          <Stat n={totals.pending || 0} l="coming soon" />
          <Stat n={totals.total_waitlist_teaser || 0} l="on the waitlist" />
        </div>
      </section>

      {/* NZ Map */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-14">
        <NZTerritoryMap
          areas={areas}
          activeKey={activeKey}
          onPinClick={(a) => setActiveKey((k) => (k === `${a.region}__${a.city || ""}` ? null : `${a.region}__${a.city || ""}`))}
          size="large"
        />
        {activeKey && (
          <div className="text-center mt-4 font-serif italic text-[var(--bloom-ink-soft)]">
            <button onClick={() => setActiveKey(null)} className="underline" data-testid="clear-pin">Show all pins</button>
          </div>
        )}
      </section>

      {/* Open areas */}
      {grouped.open.length > 0 && (
        <Section eyebrow="Open" title="These growers are taking subscribers now">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="open-areas">
            {grouped.open.map((a) => (
              <AreaCard key={a.id} a={a} cta={
                <Link to="/waitlist" state={{ region: a.region }} className="bloom-btn bloom-btn-primary w-full justify-center" data-testid={`area-cta-${a.id}`}>
                  <Mail size={14} /> Join the {a.label} waitlist <ArrowRight size={14} />
                </Link>
              } />
            ))}
          </div>
        </Section>
      )}

      {/* Pending */}
      {grouped.pending.length > 0 && (
        <Section eyebrow="Coming soon" title="Almost there, almost open">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pending-areas">
            {grouped.pending.map((a) => (
              <AreaCard key={a.id} a={a} cta={
                <Link to="/waitlist" state={{ region: a.region }} className="bloom-btn bloom-btn-outline w-full justify-center" data-testid={`area-cta-${a.id}`}>
                  <Clock size={14} /> Save my spot
                </Link>
              } />
            ))}
          </div>
        </Section>
      )}

      {/* Looking for growers */}
      {grouped.looking.length > 0 && (
        <Section eyebrow="Looking for growers" title="No grower here yet, want to be the one?">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="looking-areas">
            {grouped.looking.map((a) => (
              <AreaCard key={a.id} a={a} cta={
                <Link to="/become-a-grower" state={{ region: a.region }} className="bloom-btn bloom-btn-ghost w-full justify-center" data-testid={`area-cta-${a.id}`}>
                  <Sprout size={14} /> Apply to grow with us <ArrowRight size={14} />
                </Link>
              } />
            ))}
          </div>
        </Section>
      )}

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 mt-10 text-center">
        <Link to="/waitlist" className="bloom-btn bloom-btn-primary" data-testid="bottom-waitlist-cta">
          <Mail size={14} /> Join the waitlist <ArrowRight size={14} />
        </Link>
      </section>

      <PublicFooter />
    </div>
  );
}

function Section({ eyebrow, title, children }) {
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--bloom-ink-muted)] mb-2">{eyebrow}</div>
        <h2 className="font-serif text-3xl md:text-4xl">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function AreaCard({ a, cta }) {
  const meta = STATUS_META[a.status] || STATUS_META.looking;
  const teaser = a.waitlist_teaser || 0;
  return (
    <div className="bloom-card p-6 flex flex-col" data-testid={`area-card-${a.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] mb-2">
            <MapPin size={11} /> {a.region}
          </div>
          <h3 className="font-serif text-3xl leading-none">{a.label}</h3>
        </div>
        <span className={`text-[9px] tracking-[0.2em] uppercase px-2 py-1 ${meta.pill}`}>{meta.label}</span>
      </div>

      <p className="font-serif italic text-sm text-[var(--bloom-ink-soft)] mb-5">{meta.desc}</p>

      {teaser > 0 && (
        <div className="flex items-center gap-2 text-sm text-[var(--bloom-ink-soft)] mb-5">
          <Sparkles size={13} className="text-[var(--bloom-brand)]" />
          <span className="font-serif">{teaser} on the waitlist</span>
        </div>
      )}

      <div className="mt-auto">{cta}</div>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div className="border-l-2 border-[var(--bloom-brand)] pl-4">
      <div className="font-serif text-4xl">{n || "0"}</div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] mt-1">{l}</div>
    </div>
  );
}
